import { createRouter, createWebHistory } from '@ionic/vue-router';
import { RouteRecordRaw } from 'vue-router';
import TabsPage from '../views/TabsPage.vue';
import { useAuth } from '@/services/AuthService';  // Import token retrieval function
import { useWorkoutStore } from '@/stores/workoutStore';
import { SchultenMedia } from '@/services/DataService';
import { useChatStore } from '@/stores/chatsStore';
import { Preferences } from "@capacitor/preferences";
import { useNotificationStore } from '@/stores/notificationStore';
import { useThemeStore } from '@/stores/themeStore';
import { useTenantStore } from '@/stores/tenantStore';
import TermsService from '@/services/TermsService';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { waitForPageEnter } from './waitForPageEnter';

const routes: Array<RouteRecordRaw> = [
    {
        path: '/',
        name: 'start',
        // The five signed-out screens on `src/theme/auth-shell.css`; see themeStore.followRoute.
        meta: { authShell: true },
        component: () => import('@/views/Authentication/Start.vue')
    },
    {
        path: '/login-options',
        name: 'LoginOptions',
        component: () => import('@/views/Authentication/LoginOption.vue')
    },
    {
        name: 'Login',  // Add login route name
        path: '/login',  // Add login route
        meta: { authShell: true },
        component: () => import('@/views/Authentication/Login.vue')
    },
    {
        name: 'Register',
        path: '/registreren',
        component: () => import('@/views/Authentication/Register.vue')
    },
    {
        // Admin debug-impersonation hand-off: exchanges a one-time code for a
        // session. Public on purpose (you arrive here unauthenticated).
        name: 'Impersonate',
        path: '/impersonate',
        component: () => import('@/views/Authentication/Impersonate.vue')
    },
    {
        name: 'ForgotPassword',  // Add login route name
        path: '/forgot-password',  // Add login route
        meta: { authShell: true },
        component: () => import('@/views/Authentication/ForgotPassword.vue')
    },
    {
        name: 'ForgotPasswordCode',  // Add login route name
        path: '/forgot-password/code',  // Add login route
        meta: { authShell: true },
        component: () => import('@/views/Authentication/ForgotPasswordCode.vue')
    },
    {
        name: 'ForgotPasswordReset',  // Add login route name
        path: '/forgot-password/reset',  // Add login route
        meta: { authShell: true },
        component: () => import('@/views/Authentication/ForgotPasswordReset.vue')
    },
    {
        name: 'Terms',
        path: '/terms',
        meta: { requiresAuth: true },
        component: () => import('@/views/Authentication/Terms.vue')
    },
    {
        name: 'Start Training',
        path: '/start-training/:id',
        meta: { requiresAuth: true },
        component: () => import('@/views/StartTraining.vue') // Not in the component as saparate tab
    },
    {
        name: 'Cardio Timer Redirect',
        path: '/cardio-timer/:uuid',
        redirect: (to: any) => `/start-training/${to.params.uuid}`,
    },
    {
        path: '/',
        component: TabsPage,
        meta: { requiresAuth: true }, // Add auth requirement here
        children: [
            {
                name: 'Home',
                path: '',
                redirect: '/dashboard'
            },
            {
                name: 'Dashboard',
                path: 'dashboard',
                component: () => import('@/views/Dashboard.vue')
            },
            {
                name: 'Habits',
                path: 'habits',
                component: () => import('@/views/Habits.vue')
            },
            {
                name: "habit-action",
                path: "/habit/:id",
                meta: { requiresAuth: true },
                component: () => import("@/views/Habit/Action.vue"),
                props: true,
            },
            {
                name: 'Voeding',
                path: 'nutrition',
                component: () => import('@/views/Nutrition.vue')
            },
            {
                name: 'Training',
                path: 'training',
                component: () => import('@/views/Training.vue')
            },
            {
                name: 'Agenda',
                path: 'calendar',
                component: () => import('@/views/Calendar.vue')
            },
            {
                name: 'invitation',
                path: 'invitation/:id',
                component: () => import('@/views/EventInvitation.vue')
            },
            {
                // A questionnaire is the same form as a check-in, only assigned by hand
                // instead of on a schedule, so it opens on the same screen (COACHIO-1859).
                name: "intake-form",
                path: "/intake/:id",
                meta: { requiresAuth: true },
                component: () => import("@/views/Forms/CheckinForm.vue"),
                props: true,
            },
            {
                name: "checkin-form",
                path: "/checkin/:id",
                meta: { requiresAuth: true },
                component: () => import("@/views/Forms/CheckinForm.vue"),
                props: true,
            },
            {
                name: "checkins",
                path: "/checkins",
                meta: { requiresAuth: true },
                component: () => import("@/views/Forms/AllCheckins.vue"),
                props: true,
            },
            {
                name: "metrics",
                path: "/metrics",
                meta: { requiresAuth: true },
                component: () => import("@/views/Metrics.vue"),
                props: true,
            },
            {
                name: 'metrics-overview',
                path: '/metrics/detail/:user_metric_id',
                meta: { requiresAuth: true },
                component: () => import('@/views/MetricOverview.vue'),
                props: true,
            },
        ]
    },
    {
        name: 'chat',
        path: '/chat',
        meta: { requiresAuth: true },
        component: () => import('@/views/Chat/ChatPage.vue')
    },
    {
        name: 'chat-detail',
        path: '/chat/detail/:uuid',
        meta: { requiresAuth: true },
        component: () => import('@/views/Chat/DetailPage.vue'),
        props: true,
    },
    {
        path: '/account',
        redirect: '/account/settings',
    },
    {
        name: 'account-settings',
        path: '/account/settings',
        meta: { requiresAuth: true },
        component: () => import('@/views/Account/settings.vue'),
        props: true,
    },
    {
        name: 'knowledge-base',
        path: '/knowledge-base',
        meta: { requiresAuth: true },
        component: () => import('@/views/Knowledge/KnowledgeBase.vue'),
        props: true,
    },
    {
        name: 'knowledge-articles',
        path: '/knowledge-base/:categoryUuid/articles',
        meta: { requiresAuth: true },
        component: () => import('@/views/Knowledge/KnowledgeArticles.vue'),
        props: true,
    },
    {
        name: 'knowledge-article-detail',
        path: '/knowledge-base/:categoryUuid/articles/:articleUuid',
        meta: { requiresAuth: true },
        component: () => import('@/views/Knowledge/KnowledgeArticleDetail.vue'),
        props: true,
    },
    {
        name: 'progress-report',
        path: '/progress',
        meta: { requiresAuth: true },
        component: () => import('@/views/ProgressReport.vue'),
        props: true,
    },
    {
        path: '/weekly-summary',
        redirect: (to) => ({ path: '/progress', query: to.query, hash: to.hash }),
    },
    // Bookings
    {
        name: 'bookings-roster',
        path: '/bookings/roster',
        meta: { requiresAuth: true },
        component: () => import('@/views/Bookings/Roster.vue'),
    },
    {
        name: 'bookings-my',
        path: '/bookings/my',
        meta: { requiresAuth: true },
        component: () => import('@/views/Bookings/MyBookings.vue'),
    },
    {
        name: 'bookings-credits',
        path: '/bookings/credits',
        meta: { requiresAuth: true },
        component: () => import('@/views/Bookings/Credits.vue'),
    },
    {
        name: 'bookings-orders',
        path: '/bookings/orders',
        meta: { requiresAuth: true },
        component: () => import('@/views/Bookings/Orders.vue'),
    },
    // Legacy URLs used by booking emails. Keep these redirects so opening a
    // mail link directly still resolves to an Ionic page instead of an empty
    // router outlet. Explicitly retain parameters added by the backend/mail
    // provider.
    {
        path: '/account/credits',
        redirect: (to) => ({
            name: 'bookings-credits',
            query: to.query,
            hash: to.hash,
        }),
    },
    {
        path: '/account/booking/reservations',
        redirect: (to) => ({
            name: 'bookings-my',
            query: to.query,
            hash: to.hash,
        }),
    },
    {
        path: '/account/orders/:uuid',
        redirect: '/bookings/orders',
    },
    // Settings > Voeding > Mijn voeding: the client's own foods and recipes.
    {
        name: 'nutrition-mine',
        path: '/nutrition/mine',
        meta: { requiresAuth: true },
        component: () => import('@/views/Nutrition/MyFood.vue'),
    },
    // Women's health cycle tracking (tenant-gated, see the cycle guard below).
    {
        name: 'cycle',
        path: '/cycle',
        meta: { requiresAuth: true },
        component: () => import('@/views/Cycle/CycleOverview.vue'),
    },
    {
        // Pregnancy is managed from here too: there is no separate pregnancy route.
        name: 'cycle-settings',
        path: '/cycle/settings',
        meta: { requiresAuth: true },
        component: () => import('@/views/Cycle/CycleSettings.vue'),
    },
    // Periods the client cannot train, or only with limits (COACHIO-1096).
    // Opened from the settings and from the dashboard card.
    {
        name: 'availability',
        path: '/availability',
        meta: { requiresAuth: true },
        component: () => import('@/views/AvailabilityPage.vue'),
    },
];

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes
});

// Deep link handler
App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
    try {
        // Parse the URL instead of matching a specific host so universal links
        // work on web.coachio.digital, web-dev.coachio.digital and white-label
        // domains alike.
        const url = new URL(event.url);
        router.push(`${url.pathname}${url.search}${url.hash}`);
    } catch (error) {
        console.warn('Ignoring invalid app URL:', event.url, error);
    }
});

// A quick back or tab tap must not land while the previous page is still about
// to animate in, or Ionic shows the wrong page (see waitForPageEnter).
router.beforeEach((_to, from) => waitForPageEnter(from));

// Route guard to check for authentication
type RouteMeta = {
    requiresAuth: boolean;
};

router.beforeEach(async (to, from, next) => {
    const { isAuthenticated, checkAuth } = useAuth();
    const notificationStore = useNotificationStore();
    const requiresAuth = (to.meta as RouteMeta).requiresAuth;

    // Always close the notification drawer when navigating to all pages
    notificationStore.closeDrawer();

    // If route requires authentication
    if (requiresAuth) {
        // Check if already authenticated
        if (!SchultenMedia.Authenticated) {
            const authResult = await checkAuth();

            if (!authResult) {
                next({ name: "Login" });
                return;
            }
        }

        // Load theme preference after authentication
        const themeStore = useThemeStore();
        themeStore.loadPreference();

        // Initialize notifications for authenticated routes
        // Only force refresh when coming from login
        await notificationStore.fetchNotifications(undefined, from.name === 'Login');

        // Check for terms acceptance
        if (to.name !== 'Terms') {
            const needsAcceptance = await TermsService.needsAcceptance();
            if (needsAcceptance) {
                next({ name: 'Terms' });
                return;
            }
        }

        // Block the training pages when the tenant has training disabled.
        if (to.name === 'Training' || to.name === 'Start Training') {
            const tenantStore = useTenantStore();
            await tenantStore.checkFeatures();
            if (!tenantStore.trainingEnabled) {
                next({ name: 'Dashboard' });
                return;
            }
        }

        // Block the cycle pages when the tenant has women's health disabled.
        if (to.name === 'cycle' || to.name === 'cycle-settings') {
            const tenantStore = useTenantStore();
            await tenantStore.checkFeatures();
            if (!tenantStore.cycleEnabled) {
                next({ name: 'Dashboard' });
                return;
            }
        }

        // Block the credits page when the tenant has no credit system. Its
        // entry points are hidden, but a deeplink or the back stack still
        // lands on a screen about a balance nobody can top up.
        if (to.name === 'bookings-credits') {
            const tenantStore = useTenantStore();
            await tenantStore.checkFeatures();
            if (!tenantStore.creditsEnabled) {
                next({ name: 'Dashboard' });
                return;
            }
        }

        next();
        return;
    }

    if (!SchultenMedia.Authenticated) {
        //   check local storage for the token
        const token = await Preferences.get({ key: 'auth_token' });

        if (token.value) {
            const authResult = await checkAuth();
            if (authResult) {
                SchultenMedia.Authenticated = true;
            }
        }
    }

    // If we're going to auth pages while already authenticated, redirect to dashboard
    if (!requiresAuth && SchultenMedia.Authenticated && (to.name === "Login" || to.name === "Register" || to.name === "start" || to.name === "LoginOptions")) {
        next({ name: "Dashboard" });
        return;
    }

    // For non-protected routes, proceed normally
    next();
});


export default router;
